import { Info, Link2 } from "lucide-react";
import AuthBrandPanel from "./AuthBrandPanel";
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { HttpError } from "../../services/httpService";
import { getOAuthAccount, getOAuthClientMetadata, OAuthAccount, OAuthClientMetadata, selectOAuthAccount, submitOAuthConsent } from "../../services/oauthService";
import { getOAuthQuery, isLoopbackOAuthUrl, navigateToOAuthResponseUrl } from "../../utils/oauthContinuation";

const scopeToDescription: Record<string, string> = {
  "investor:read": "Read your investment strategy, positions, account balances, and products visible to you",
  offline_access: "Stay connected without logging in again",
};

interface ConsentCompletion {
  accepted: boolean;
  callbackUrl: string;
  callbackOpened: boolean;
}

function OAuthConsentCompletion({ completion, clientName }: { completion: ConsentCompletion; clientName: string }) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => headingRef.current?.focus(), []);

  return (
    <section aria-labelledby="oauth-completion-title" className="w-full max-w-lg rounded-v7-panel border border-v7-ink/15 bg-v7-white p-6 md:p-8">
      <div aria-hidden="true" className="mb-6 grid h-12 w-12 place-items-center rounded-v7-panel border border-v7-ink/15 bg-v7-paper text-v7-bronze-dark"><Link2 className="h-6 w-6" /></div>
      <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.14em] text-v7-bronze-dark">Plutus app connection</p>
      <h1 ref={headingRef} tabIndex={-1} id="oauth-completion-title" className="text-[30px] font-light leading-9 tracking-[-0.04em] text-v7-ink outline-none">{completion.accepted ? "Permission granted" : "Permission declined"}</h1>
      <p className="mt-4 break-words text-sm text-v7-ink-soft">
        {completion.accepted
          ? `You approved access for ${clientName}. Check your app to confirm the connection.`
          : `You declined this request from ${clientName}.`}
      </p>
      {!completion.callbackOpened && (
        <p className="mt-4 text-sm text-v7-ink-soft">Your browser did not open the other tab. Use the link below to return to your app.</p>
      )}
      <div className="mt-6 flex flex-wrap gap-3">
        {!completion.callbackOpened && (
          <a href={completion.callbackUrl} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center rounded-v7-control border border-v7-ink/15 bg-v7-white px-4 text-sm text-v7-ink-soft transition-colors hover:border-v7-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v7-bronze-dark">{completion.accepted ? "Finish connecting" : "Return to your app"}</a>
        )}
        <Link to="/" className="inline-flex min-h-11 items-center rounded-v7-control bg-v7-ink px-4 text-sm font-medium text-v7-white transition-colors hover:bg-v7-ink-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v7-bronze-dark">Continue to Plutus</Link>
      </div>
    </section>
  );
}

function openCallbackTab(): Window | null {
  try {
    const callbackWindow = window.open("about:blank", "_blank");
    if (callbackWindow) callbackWindow.opener = null;
    return callbackWindow;
  } catch {
    return null;
  }
}

function getConsentErrorMessage(consentError: unknown): string {
  const status = (consentError as HttpError)?.status;
  if (status === 401) return "Your sign-in session is missing or expired. Restart the connection from your app and sign in again.";
  if (status === 409) return "Your Plutus account changed in another tab. Restart the connection from your app to choose the account again.";
  if (status === 400) return "This authorization request is invalid or expired. Restart the connection from your app.";
  if (status === 404) return "This application is unavailable. Restart the connection from your app.";
  return "Could not complete authorization. Please try again or restart the connection from your app.";
}

function OAuthConsentRequest({ oauthQuery, selectAccount }: { oauthQuery: string | undefined; selectAccount: boolean }) {
  const authorizationParameters = new URLSearchParams(oauthQuery);
  const clientId = authorizationParameters.get("client_id");
  const requestedScopes = Array.from(new Set((authorizationParameters.get("scope") || "").split(/\s+/).filter(Boolean)));
  const validRequest = Boolean(oauthQuery && clientId && authorizationParameters.get("sig")
    && authorizationParameters.getAll("client_id").length === 1
    && authorizationParameters.getAll("scope").length === 1
    && requestedScopes.length > 0);
  const unsupportedScopes = requestedScopes.some(requestedScope => !Object.prototype.hasOwnProperty.call(scopeToDescription, requestedScope));
  const [providerAccount, setProviderAccount] = useState<OAuthAccount | null>(null);
  const [clientMetadata, setClientMetadata] = useState<OAuthClientMetadata | null>(null);
  const [errorMessage, setErrorMessage] = useState(validRequest ? "" : "Invalid authorization request. Restart the connection from your app.");
  const [isLoading, setIsLoading] = useState(validRequest);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [completion, setCompletion] = useState<ConsentCompletion | null>(null);
  const consentSubmittedRef = useRef(false);
  const pendingCallbackWindowRef = useRef<Window | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      pendingCallbackWindowRef.current?.close();
      pendingCallbackWindowRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!validRequest || !clientId) return;
    const requestController = new AbortController();
    Promise.all([
      getOAuthClientMetadata(clientId, requestController.signal),
      getOAuthAccount(requestController.signal),
    ])
      .then(([registeredClient, authorizationAccount]) => {
        if (requestController.signal.aborted) {
          return;
        }
        setClientMetadata(registeredClient);
        setProviderAccount(authorizationAccount);
      })
      .catch(consentError => {
        if (!requestController.signal.aborted) setErrorMessage(getConsentErrorMessage(consentError));
      })
      .finally(() => {
        if (!requestController.signal.aborted) setIsLoading(false);
      });
    return () => requestController.abort();
  }, [clientId, validRequest]);

  const handleConsent = async (accept: boolean) => {
    if (!oauthQuery || !clientMetadata || !providerAccount || consentSubmittedRef.current || (accept && unsupportedScopes)) return;
    consentSubmittedRef.current = true;
    setIsSubmitting(true);
    setErrorMessage("");
    // Reserve a tab during the click. The hinted URI chooses presentation only;
    // navigation uses the validated provider response after consent succeeds.
    if (isLoopbackOAuthUrl(authorizationParameters.get("redirect_uri"))) {
      pendingCallbackWindowRef.current = openCallbackTab();
    }
    try {
      const callbackUrl = selectAccount && accept
        ? await selectOAuthAccount(oauthQuery, providerAccount.id)
        : await submitOAuthConsent(accept, oauthQuery, providerAccount.id);
      if (!isMountedRef.current) return;
      const callbackWindow = pendingCallbackWindowRef.current;
      pendingCallbackWindowRef.current = null;
      if (!isLoopbackOAuthUrl(callbackUrl)) {
        callbackWindow?.close();
        navigateToOAuthResponseUrl(callbackUrl);
        return;
      }
      let callbackOpened = false;
      if (callbackWindow && !callbackWindow.closed) {
        try {
          callbackWindow.location.replace(callbackUrl);
          callbackOpened = true;
        } catch {
          callbackWindow.close();
        }
      }
      setCompletion({ accepted: accept, callbackUrl, callbackOpened });
    } catch (consentError) {
      pendingCallbackWindowRef.current?.close();
      pendingCallbackWindowRef.current = null;
      if (!isMountedRef.current) return;
      setErrorMessage(getConsentErrorMessage(consentError));
      consentSubmittedRef.current = false;
      setIsSubmitting(false);
    }
  };

  if (completion && clientMetadata) {
    return <OAuthConsentCompletion completion={completion} clientName={clientMetadata.client_name || clientMetadata.client_id} />;
  }

  return (
    <section aria-labelledby="oauth-consent-title" aria-busy={isLoading || isSubmitting} className="w-full max-w-lg rounded-v7-panel border border-v7-ink/15 bg-v7-white p-6 md:p-8">
      <div aria-hidden="true" className="mb-6 grid h-12 w-12 place-items-center rounded-v7-panel border border-v7-ink/15 bg-v7-paper text-v7-bronze-dark"><Link2 className="h-6 w-6" /></div>
      <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.14em] text-v7-bronze-dark">Plutus app connection</p>
      <h1 id="oauth-consent-title" className="text-[30px] font-light leading-9 tracking-[-0.04em] text-v7-ink">{selectAccount ? "Choose your Plutus account" : "Connect to Plutus"}</h1>
      {isLoading && <p role="status" className="mt-4 text-sm text-v7-ink-soft">Loading application details...</p>}
      {clientMetadata && providerAccount && (
        <div className="mt-4 space-y-4">
          <p className="break-words text-sm text-v7-ink-soft">{selectAccount ? "You can connect with" : "Connecting as"} <strong className="text-v7-ink">{providerAccount.email}</strong>.</p>
          <p className="break-words text-sm text-v7-ink-soft">
            <strong className="text-v7-ink">{clientMetadata.client_name || clientMetadata.client_id}</strong> wants permission to:
          </p>
          <ul className="list-disc space-y-3 border-y border-v7-ink/15 py-5 pl-5 text-sm leading-6 text-v7-ink-soft">
            {requestedScopes.map(requestedScope => (
              <li key={requestedScope} className="break-words">
                {Object.prototype.hasOwnProperty.call(scopeToDescription, requestedScope)
                  ? scopeToDescription[requestedScope]
                  : `Unsupported permission: ${requestedScope}`}
              </li>
            ))}
          </ul>
          <div className="flex items-start gap-2.5 border-l-2 border-v7-bronze bg-v7-paper p-3 text-xs leading-[17px] text-v7-ink/60">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-v7-bronze-dark" aria-hidden="true" />
            <p>The application name is supplied by its developer. It is not a verified vendor identity. Only approve an application you trust.</p>
          </div>
          {unsupportedScopes && <p className="text-sm text-red-700">This request includes unsupported permissions and cannot be approved.</p>}
          <div className={selectAccount ? "flex flex-col gap-3 sm:flex-row" : "flex gap-3"}>
            <button type="button" disabled={isSubmitting} onClick={() => void handleConsent(false)} className="min-h-11 flex-1 rounded-v7-control border border-v7-ink/15 bg-v7-white px-4 text-sm text-v7-ink-soft transition-colors hover:border-v7-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v7-bronze-dark disabled:cursor-not-allowed disabled:opacity-50">{selectAccount ? "Cancel" : "Deny"}</button>
            <button type="button" disabled={isSubmitting || unsupportedScopes} aria-label={selectAccount ? `Continue as ${providerAccount.email}` : undefined} onClick={() => void handleConsent(true)} className="min-h-11 min-w-0 flex-1 break-words rounded-v7-control bg-v7-ink px-4 py-2 text-sm font-medium text-v7-white transition-colors hover:bg-v7-ink-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v7-bronze-dark disabled:cursor-not-allowed disabled:opacity-50">{selectAccount ? "Continue as this account" : "Approve"}</button>
          </div>
          {selectAccount && !isSubmitting && (
            <Link to={`/login?${new URLSearchParams({ oauth_query: oauthQuery ?? "" })}`} className="inline-flex min-h-11 items-center text-sm text-v7-bronze-dark underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-v7-bronze-dark">Use another account</Link>
          )}
          {isSubmitting && <p role="status" className="text-sm text-v7-ink-soft">Returning to your app...</p>}
        </div>
      )}
      {errorMessage && <p role="alert" className="mt-4 rounded-v7-control border border-red-200 bg-red-50 p-3 text-sm text-red-700">{errorMessage}</p>}
    </section>
  );
}

export default function OAuthConsentPage({ selectAccount = false }: { selectAccount?: boolean }) {
  const { search } = useLocation();
  const oauthQuery = getOAuthQuery(search);
  return (
    <div className="grid min-h-dvh bg-v7-canvas font-v7-sans text-v7-ink transition-colors duration-300 lg:grid-cols-[minmax(380px,0.88fr)_minmax(560px,1.12fr)]">
      <a href="#oauth-content" className="fixed left-3 top-3 z-[1000] -translate-y-24 border border-v7-ink bg-v7-white px-3.5 py-2.5 text-[13px] text-v7-ink transition-transform duration-200 focus:translate-y-0">
        Skip to permissions
      </a>
      <div className="hidden lg:grid">
        <AuthBrandPanel headline={<>Your portfolio.<br /><span className="font-v7-serif font-normal italic">Connected.</span></>} />
      </div>
      <main id="oauth-content" className="flex min-h-dvh min-w-0 flex-col bg-v7-canvas px-4 py-6 font-v7-sans text-v7-ink sm:px-8 lg:px-10 lg:py-9">
        <header className="flex min-h-11 items-center justify-between gap-3 lg:justify-end">
          <Link to="/" aria-label="Plutus home" className="inline-flex min-h-11 items-center gap-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark lg:hidden">
            <img src="/favicon.png" alt="" className="h-9 w-9 object-contain" />
            <span className="text-xs font-medium tracking-[0.24em]">PLUTUS</span>
          </Link>
          <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-v7-ink/50">App permissions</span>
        </header>
        <div className="mx-auto my-auto w-full max-w-lg py-8 lg:py-10">
          <OAuthConsentRequest key={oauthQuery ?? "missing"} oauthQuery={oauthQuery} selectAccount={selectAccount} />
        </div>
      </main>
    </div>
  );
}
